import { Badge, Collapse, Group, Loader, Stack, Text, UnstyledButton } from '@mantine/core';
import { useState } from 'react';
import type { AiReviewSummary } from '../api/cases';
import {
  categoryColor,
  isTerminal,
  prettyClassification,
  statusColor,
  statusLabel,
} from './ai-review-status';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

interface Props {
  reviews: AiReviewSummary[]; // newest first, already filtered to one finding
  optimisticQueued: boolean;
}

export function FindingAiPanel({ reviews, optimisticQueued }: Props) {
  const [showHistory, setShowHistory] = useState(false);
  const latest = reviews[0] ?? null;
  const hasNonTerminal = reviews.some((r) => !isTerminal(r.status));

  // Optimistic Queued wins only until a real non-terminal review exists.
  if (optimisticQueued && !hasNonTerminal) {
    return (
      <Panel>
        <Group gap="xs">
          <Loader size="xs" />
          <Badge variant="light" color="blue">
            Queued
          </Badge>
          <Text size="xs" c="dimmed">
            Review requested — waiting for the engine to pick it up…
          </Text>
        </Group>
      </Panel>
    );
  }

  if (!latest) {
    return (
      <Panel>
        <Text size="xs" c="dimmed">
          No AI review yet for this lesion.
        </Text>
      </Panel>
    );
  }

  const inflight = !isTerminal(latest.status);
  const older = reviews.slice(1);

  return (
    <Panel failed={latest.status === 'failed'}>
      <Group justify="space-between">
        <Group gap="xs">
          {inflight && <Loader size="xs" />}
          <Badge variant="light" color={statusColor(latest.status)}>
            {statusLabel(latest.status)}
          </Badge>
          {latest.status === 'failed' && (latest.errorMessage || latest.errorClassification) && (
            <Text size="xs" c="red">
              {latest.errorMessage ?? latest.errorClassification}
            </Text>
          )}
        </Group>
        <Text size="xs" c="dimmed">
          {formatDate(latest.completedAt ?? latest.createdAt)}
        </Text>
      </Group>

      {latest.status === 'completed' && <ReviewResultLines review={latest} mt={6} />}

      {older.length > 0 && (
        <>
          <UnstyledButton
            onClick={() => setShowHistory((s) => !s)}
            mt={6}
            aria-expanded={showHistory}
          >
            <Text size="xs" c="dimmed">
              {showHistory ? '▾' : '▸'} {older.length} previous run{older.length > 1 ? 's' : ''}
            </Text>
          </UnstyledButton>
          <Collapse in={showHistory}>
            <Stack gap={6} mt={4}>
              {older.map((r) => (
                // Keep the status label (e.g. "Superseded") and show that run's
                // actual prior result underneath — superseded rows retain their
                // lesions/result from before they were superseded.
                <Stack key={r.id} gap={2}>
                  <Group gap="xs">
                    <Badge size="xs" variant="light" color={statusColor(r.status)}>
                      {statusLabel(r.status)}
                    </Badge>
                    <Text size="xs" c="dimmed">
                      {formatDate(r.completedAt ?? r.createdAt)}
                    </Text>
                  </Group>
                  <ReviewResultLines review={r} />
                  {r.status === 'failed' && (r.errorMessage || r.errorClassification) && (
                    <Text size="xs" c="red">
                      {r.errorMessage ?? r.errorClassification}
                    </Text>
                  )}
                </Stack>
              ))}
            </Stack>
          </Collapse>
        </>
      )}
    </Panel>
  );
}

/**
 * The per-lesion classification/category/referral/codes for a review's results.
 * Shared by the latest review and each "previous runs" history entry. Renders
 * nothing when the review has no lesions (e.g. a review superseded while still
 * pending, or a failed run).
 */
function ReviewResultLines({ review, mt }: { review: AiReviewSummary; mt?: number }) {
  const lesions = review.lesions ?? [];
  if (lesions.length === 0) return null;
  return (
    <Stack gap={2} mt={mt}>
      {lesions.map((l) => (
        <Group key={l.id} gap="xs">
          <Text size="sm">{prettyClassification(l.classification)}</Text>
          <Badge size="sm" variant="light" color={categoryColor(l.category)}>
            {l.category}
          </Badge>
          {l.referralFlag && (
            <Text size="xs" c="dimmed">
              referral: {l.referralFlag}
            </Text>
          )}
          {l.suspectedDiagnosisSnomedCode && (
            <Text size="xs" c="dimmed">
              SNOMED {l.suspectedDiagnosisSnomedCode}
            </Text>
          )}
          {l.suspectedDiagnosisIcd10Code && (
            <Text size="xs" c="dimmed">
              ICD-10 {l.suspectedDiagnosisIcd10Code}
            </Text>
          )}
        </Group>
      ))}
    </Stack>
  );
}

function Panel({ children, failed }: { children: React.ReactNode; failed?: boolean }) {
  return (
    <Stack
      gap={4}
      p="sm"
      style={{
        border: '1px dashed var(--mantine-color-default-border)',
        borderRadius: 8,
        background: failed ? 'var(--mantine-color-red-light)' : 'var(--mantine-color-grape-light)',
      }}
    >
      {children}
    </Stack>
  );
}
